import { ServiceProvider } from "Service";
import { Page } from "./Page";
import {
    ColumnTypes,
    FormColumnDefinition,
    FormDefinition,
    GridControlTypes,
    GridTypes,
    HorizontalAlignment,
    ReportParameter,
    ReportParameterValue,
} from "Service/Client";
import { AppContext } from "Core/AppContext";
import { Configuration } from "Configuration";
import { Filter } from "Grids/Filter";
import { Util } from "Core/Util";

export class ReportPage extends Page {
    private view: kendo.View;

    private filter: Filter;
    private parameters: ReportParameter[];

    private toolbar: kendo.ui.ToolBar;

    constructor(private reportName: string, serviceProvider: ServiceProvider) {
        super(serviceProvider);
    }

    async render(layout: kendo.Layout): Promise<void> {
        await super.initialize("reports");

        this.view = new kendo.View(
            `<div class="dms-mapping-header">
                <h1 data-bind="html: getTextBlockpageTitle"></h1>
                <div data-bind="html: getTextBlockpageContent" class="dms-textblock dms-pagecontent"></div>
            </div>

             <div class="row col-12">
                <div class="col-sm-12">
                    <div id="dms-reports-parameter" class="dms-grid-wrapper dms-filter"></div>
                </div>
            </div>
            <div class="row col-12">
                <div class="col-sm-12">
                    <div class="k-grid-toolbar dms-reports-toolbar" style="visibility: hidden"></div>
                </div>
            </div>
            <div class="row col-12">
                <div id="dms-reports-content">
                </div>
            </div>`,
            {
                model: this.viewModel,
                evalTemplate: true,
                wrap: false,
                hide: () => {},
            }
        );

        layout.showIn(".dms-content-container", this.view);
        this.parameters = await this.serviceProvider.ReportService.getParameters(this.reportName);

        const element = $("#dms-reports-parameter");

        const filterDefinition: FormDefinition = {
            GridTypeID: GridTypes.Filter,
            GridID: -1,
            GridName: "Report_Filter",
            ShowDefault: true,
            Groups: [],
            Buttons: [],
            Submitbuttons: [],
            ButtonPlacementID: 1,
            GridWidth: "40em",
        };
        const submitButton: FormColumnDefinition = {
            ColumnHeader: " ",
            ColumnName: "Submit",
            ColumnTypeID: ColumnTypes.Submitbutton,
            Controls: [],
            AlignmentID: HorizontalAlignment.Right,
        };
        submitButton.Controls.push({
            ControlID: -1,
            ControlTypeID: GridControlTypes.Command,
            Script: "app.router.currentPage.updateReport();",
            IsSubmit: true,
            IsDefaultButton: true,
            OnEditEditable: true,
            OnAddEditable: true,
            OnEdit: true,
            Caption: "viewReport",
            CheckRequiredFields: true,
        });
        filterDefinition.Submitbuttons.push(submitButton);

        filterDefinition.Columns = [];
        let filterId = -2;
        filterDefinition.Columns = this.parameters.map((parameter) => {
            const col: FormColumnDefinition = {
                ColumnHeader: parameter.Name,
                ColumnTypeID: ColumnTypes.Standard,
                ColumnName: parameter.Name,
                Controls: [],
            };
            col.Controls.push({
                ControlID: filterId--,
                ControlTypeID: parameter.Type,
                OnEdit: true,
                OnEditEditable: true,
                ValueColumnName: parameter.Name,
                EditRequiredText: !parameter.Nullable && !parameter.AllowBlank ? "parameterRequired" : undefined,
            });
            return [col];
        });

        const gridTypeConfiguration = Configuration.getGridTypeConfiguration(GridTypes.Filter);
        this.filter = <Filter>(
            gridTypeConfiguration.factory(
                filterDefinition,
                "reports",
                gridTypeConfiguration.gridSettings,
                this.serviceProvider,
                AppContext.current.environment
            )
        );

        await this.filter.setElement(element);
        await this.filter.initialLoad();

        const toolbarElement = $(".dms-reports-toolbar");
        this.toolbar = new kendo.ui.ToolBar(toolbarElement[0], {
            items: [
                {
                    type: "splitButton",
                    spriteCssClass: "far fa-floppy-disk",
                    id: "exportPdf",
                    menuButtons: [
                        {
                            id: "exportPdf",
                            text: this.serviceProvider.TextBlock.get("exportPdf", "reports"),
                        },
                        {
                            id: "exportXlsx",
                            text: this.serviceProvider.TextBlock.get("exportXlsx", "reports"),
                        },
                    ],
                },
            ],
            click: (e) => this.onToolbarClick(e),
        });

        if (AppContext.current.environment.AutoCreateTextBlocksIfMissing) {
            setTimeout(() => this.serviceProvider.TextBlock.setTextBlocks(AppContext.current.environment.LanguageCode), 2000);
        }
    }

    async updateReport() {
        this.filter.setBusy(true);

        const toolbarElement = $(".dms-reports-toolbar");
        toolbarElement.css("visibility", "visible");

        const contentElement = $("#dms-reports-content");

        const parameterValues = this.getParameterValues();

        const content = await this.serviceProvider.ReportService.getContent(this.reportName, parameterValues);
        contentElement.html(content.Data);
        this.filter.setBusy(false);
    }

    private async onToolbarClick(e: kendo.ui.ToolBarClickEvent) {
        let format: string = null;
        if (e.id === "exportPdf") {
            format = "pdf";
        } else if (e.id === "exportXlsx") {
            format = "xlsx";
        }

        if (format) {
            this.filter.setBusy(true);
            const parameterValues = this.getParameterValues();
            const data = await this.serviceProvider.ReportService.exportReport(this.reportName, parameterValues, format);
            this.filter.setBusy(false);
            Util.saveFileBase64(data.Data, data.MimeType, data.FileName);
        }
    }

    private getParameterValues(): ReportParameterValue[] {
        const parameterValues: ReportParameterValue[] = [];
        const filterData = this.filter.getData();
        for (const parameter of this.parameters) {
            parameterValues.push({
                Name: parameter.Name,
                Value: filterData[parameter.Name]?.toString(),
            });
        }
        return parameterValues;
    }

    dispose(): void {
        if (this.headerView) {
            this.headerView.destroy();
        }
        if (this.filter) {
            this.filter.dispose();
        }
        if (this.toolbar) {
            this.toolbar.destroy();
        }
        if (this.view) {
            this.view.destroy();
        }
    }
}
